/*
 * Copyright (c) 2022 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * Color configuration for permission codes
 */
export interface ColorConfig {
	backgroundColor: string;
	color: string;
}

/**
 * Permission code display order
 * Defines the order in which permission codes should be displayed/sorted
 */
export const PermissionOrder: readonly string[] = ["C1", "H1", "R1", "L1", "L2", "S1"] as const;

/**
 * Permission code to color mapping
 * Maps each permission code to its display color configuration
 */
export const PermissionColors: Record<string, ColorConfig> = {
	C1: { backgroundColor: "#1976d2", color: "#ffffff" }, // Blue
	L1: { backgroundColor: "#ff9800", color: "#ffffff" }, // Orange (lightened)
	L2: { backgroundColor: "#e65100", color: "#ffffff" }, // Dark Orange (darkened)
	H1: { backgroundColor: "#ab47bc", color: "#ffffff" }, // Purple (lightened)
	R1: { backgroundColor: "#6a1b9a", color: "#ffffff" }, // Dark Purple (darkened)
	S1: { backgroundColor: "#00796b", color: "#ffffff" }, // Teal
} as const;

/**
 * Default color configuration for unmapped permission codes
 */
const defaultColorConfig: ColorConfig = {
	backgroundColor: "#9e9e9e",
	color: "#ffffff",
};

/**
 * Gets the sort order for a permission code
 * Returns the index in PermissionOrder, or Infinity if not found (to sort unmapped codes to the end)
 * 
 * @param permissionCode - The permission code to get the sort order for
 * @returns The sort order index, or Infinity if not found
 */
export function GetPermissionSortOrder(permissionCode?: string): number {
	if (!permissionCode) {
		return Infinity;
	}
	const index = PermissionOrder.indexOf(permissionCode);
	return index === -1 ? Infinity : index;
}

/**
 * Maps permission codes to colors
 * Returns the color configuration for the specified code, or default gray if not found
 * 
 * @param permissionCode - The permission code to get colors for
 * @returns Color configuration object with backgroundColor and color
 */
export function GetPermissionColorConfig(permissionCode?: string): ColorConfig {
	if (!permissionCode) {
		return defaultColorConfig;
	}
	
	// Return the color for this code, or default gray if not mapped
	return PermissionColors[permissionCode] || defaultColorConfig;
}

/**
 * Formats the permission label for display
 * Currently returns the object name, but can be extended to include permission code formatting
 * 
 * @param objectName - The name of the permission object
 * @param permissionCode - Optional permission code (currently unused but available for future use)
 * @returns The formatted permission label
 */
export function GetPermissionLabel(objectName: string, permissionCode?: string): string {
	// Currently just returns the object name
	// Can be extended in the future to format with permission code if needed
	return objectName;
}
